import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import App from './App';
import './index.css';
import './styles/splash-isolation.css';
import { logger } from './utils/logger';

// Expose React to window for sharing with iframe
// This allows the splash screen iframe to use the same React instance
// and avoid the isPrimaryRenderer error
if (typeof window !== 'undefined') {
  // Try to access React internals through multiple paths
  // React 19 uses __CLIENT_INTERNALS_DO_NOT_USE_OR_WARN_USERS_THEY_CANNOT_UPGRADE
  // but Next.js expects __SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED
  let reactInternals: any = null;
  
  // Try React 19's client internals first
  if ((React as any).__CLIENT_INTERNALS_DO_NOT_USE_OR_WARN_USERS_THEY_CANNOT_UPGRADE) {
    reactInternals = (React as any).__CLIENT_INTERNALS_DO_NOT_USE_OR_WARN_USERS_THEY_CANNOT_UPGRADE;
  }
  // Try legacy secret internals (for compatibility)
  else if ((React as any).__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED) {
    reactInternals = (React as any).__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED;
  }
  // Try through ReactDOM
  else if ((ReactDOM as any).__CLIENT_INTERNALS_DO_NOT_USE_OR_WARN_USERS_THEY_CANNOT_UPGRADE) {
    reactInternals = (ReactDOM as any).__CLIENT_INTERNALS_DO_NOT_USE_OR_WARN_USERS_THEY_CANNOT_UPGRADE;
  }
  else if ((ReactDOM as any).__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED) {
    reactInternals = (ReactDOM as any).__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED;
  }
  
  // Expose React with all its internals
  // CRITICAL: Use direct reference to preserve React's internal module structure
  const reactToExpose = React as any;
  const reactDOMToExpose = ReactDOM as any;
  
  // Map React 19 internals to the structure Next.js expects
  if (reactInternals) {
    // Next.js expects __SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED
    // Map React 19's __CLIENT_INTERNALS to it
    if (!reactToExpose.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED) {
      reactToExpose.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED = reactInternals;
    }
    // Also keep the React 19 version for compatibility
    if (!reactToExpose.__CLIENT_INTERNALS_DO_NOT_USE_OR_WARN_USERS_THEY_CANNOT_UPGRADE) {
      reactToExpose.__CLIENT_INTERNALS_DO_NOT_USE_OR_WARN_USERS_THEY_CANNOT_UPGRADE = reactInternals;
    }
    
    // Ensure the 'S' object exists and has Dispatcher
    // Next.js accesses React.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED.S.Dispatcher
    const secretInternals = reactToExpose.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED;
    if (secretInternals && secretInternals.S && !secretInternals.S.Dispatcher) {
      // Try to find Dispatcher in ReactDOM internals
      if (reactDOMToExpose && reactDOMToExpose.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED) {
        const domInternals = reactDOMToExpose.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED;
        if (domInternals.S && domInternals.S.Dispatcher) {
          secretInternals.S.Dispatcher = domInternals.S.Dispatcher;
        }
      }
      // If still not found, try client internals
      if (!secretInternals.S.Dispatcher && reactToExpose.__CLIENT_INTERNALS_DO_NOT_USE_OR_WARN_USERS_THEY_CANNOT_UPGRADE) {
        const clientInternals = reactToExpose.__CLIENT_INTERNALS_DO_NOT_USE_OR_WARN_USERS_THEY_CANNOT_UPGRADE;
        if (clientInternals.S && clientInternals.S.Dispatcher) {
          secretInternals.S.Dispatcher = clientInternals.S.Dispatcher;
        }
      }
    }
  }
  
  (window as any).__REACT_SHARED__ = {
    React: reactToExpose,  // Direct reference to React module
    ReactDOM: reactDOMToExpose,  // Direct reference to ReactDOM module
    // Also expose as a getter to ensure it's always current
    get ReactCurrent() {
      return React;
    },
    get ReactDOMCurrent() {
      return ReactDOM;
    }
  };
  
  // Also expose directly for easier access
  (window as any).__REACT__ = reactToExpose;
  (window as any).__REACT_DOM__ = reactDOMToExpose;
  
  // Debug: Verify React internals are accessible (logging removed for cleaner console)
  if (process.env.NODE_ENV === 'development') {
    const hasSecretInternals = reactToExpose && reactToExpose.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED;
    const hasClientInternals = reactToExpose && reactToExpose.__CLIENT_INTERNALS_DO_NOT_USE_OR_WARN_USERS_THEY_CANNOT_UPGRADE;
    const hasInternals = hasSecretInternals || hasClientInternals;
    
    if (hasInternals && hasSecretInternals) {
      const internals = reactToExpose.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED;
      // Check for Dispatcher in the 'S' key (where Next.js expects it)
      if (internals && internals.S) {
        // If Dispatcher doesn't exist, try to find it elsewhere
        if (!internals.S.Dispatcher) {
          // Check if it's in ReactDOM
          if (reactDOMToExpose && reactDOMToExpose.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED) {
            const domInternals = reactDOMToExpose.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED;
            if (domInternals.S && domInternals.S.Dispatcher) {
              // Copy Dispatcher from ReactDOM to React if needed
              if (!internals.S) {
                internals.S = {};
              }
              internals.S.Dispatcher = domInternals.S.Dispatcher;
            }
          }
        }
      }
    } else if (!hasInternals) {
      console.warn('[Main App] React internals not found. This may cause Dispatcher errors in the splash screen.');
    }
  }
}

// Suppress known React 19 isPrimaryRenderer error from splash iframe
// This is a development-only issue when React is loaded multiple times
if (process.env.NODE_ENV === 'development') {
  const originalError = window.console.error;
  window.console.error = (...args: any[]) => {
    const errorMessage = args[0]?.toString() || '';
    const isReactPrimaryRendererError = 
      errorMessage.includes('isPrimaryRenderer') ||
      errorMessage.includes('Cannot read properties of undefined (reading \'S\')') ||
      errorMessage.includes('Cannot read properties of undefined (reading \'Dispatcher\')') ||
      (errorMessage.includes('TypeError') && 
       (errorMessage.includes('reading \'S\'') || errorMessage.includes('reading \'Dispatcher\'')));
    
    // Suppress this known development-only error
    if (isReactPrimaryRendererError) {
      return;
    }
    
    // Log all other errors normally
    originalError.apply(console, args);
  };

  // Also catch global errors from iframe
  window.addEventListener('error', (event) => {
    const errorMessage = event.message || (event.error?.message || '') || '';
    const isReactPrimaryRendererError = 
      errorMessage.includes('isPrimaryRenderer') ||
      errorMessage.includes('Cannot read properties of undefined (reading \'S\')') ||
      errorMessage.includes('Cannot read properties of undefined (reading \'Dispatcher\')') ||
      (errorMessage.includes('TypeError') && 
       (errorMessage.includes('reading \'S\'') || errorMessage.includes('reading \'Dispatcher\'')));
    
    if (isReactPrimaryRendererError) {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
  }, true);
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
        <Toaster position="top-right" />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>
);
